import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/store/auth';
import { logout } from '@/api/auth';
import { readPauseReason } from '@/api/client';

/**
 * Shown when the API answers 423 — a super admin has paused this academy.
 *
 * The block is enforced server-side on every admin and manager route; this
 * page only explains it and offers the one action still available. Students
 * are never gated, but they do not use this console anyway.
 */
export default function AcademyPausedPage() {
  const navigate = useNavigate();
  const clear = useAuth((s) => s.clear);
  const reason = readPauseReason();

  async function onLogout() {
    // The pause does not invalidate the session, so logout still has to reach
    // the API to revoke the token rather than just clearing local state.
    await logout().catch(() => undefined);
    clear();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 text-center">
      <div className="max-w-md">
        <h1 className="text-2xl font-bold">Your academy is paused</h1>
        <p className="mt-3 text-grey-500">
          PartApp has paused access for your academy. You and your staff will be able to sign in
          again as soon as it is resumed. Your students are unaffected and can keep using the
          mobile app.
        </p>
        {reason ? (
          <div className="mt-5 rounded-lg bg-grey-100 p-4 text-left">
            <div className="text-xs font-semibold uppercase tracking-wide text-grey-500">
              Reason
            </div>
            <p className="mt-1 text-sm">{reason}</p>
          </div>
        ) : null}
        <p className="mt-5 text-sm text-grey-500">
          Please contact PartApp support if you think this is a mistake.
        </p>
        <div className="mt-6">
          <Button onClick={onLogout}>Log out</Button>
        </div>
      </div>
    </div>
  );
}
